// Tag for graphql format
const gql = String.raw;

// Common fragments
const ModelYearsFragment = gql`
	fragment ModelYears on ModelYears {
		rangeType
		left
		right
	}
`;

const InputValueFragment = gql`
	fragment InputValue on InputValue {
		input
		value
	}
`;

const UserFullNameFragment = gql`
	fragment UserFullName on SmallUserInformation {
		_id
		fullName
	}
`;

const BonusDefinitionFragment = gql`
	fragment BonusDefinition on BonusDefinition {
		from {
			...InputValue
		}
		to {
			...InputValue
		}
		bonus {
			...InputValue
		}
	}
`;

const CommissionTiersFragment = gql`
	fragment CommissionTiers on CommissionTiers {
		from {
			...InputValue
		}
		to {
			...InputValue
		}
		commission {
			...InputValue
		}
	}
`;

const CommissionsTiersFragment = gql`
	fragment CommissionsTiers on CommissionsTiers {
		from {
			...InputValue
		}
		to {
			...InputValue
		}
		commissions {
			...CommissionTiers
		}
	}
`;

const CommissionMakeTiersFragment = gql`
	fragment CommissionMakeTiers on CommissionMakeTiers {
		make
		dealerCodeFS
		applyTiersRetroactively
		afterChargebacks
		tiers {
			...CommissionsTiers
		}
	}
	${CommissionsTiersFragment}
`;

const BonusesTiersFragment = gql`
	fragment BonusesTiers on BonusesTiers {
		from {
			...InputValue
		}
		to {
			...InputValue
		}
		bonuses {
			...BonusDefinition
		}
	}
`;

const AdjustmentsTiersFragment = gql`
	fragment AdjustmentsTiers on AdjustmentsTiers {
		from {
			...InputValue
		}
		to {
			...InputValue
		}
		adjustment {
			...InputValue
		}
	}
`;

const MetricFragment = gql`
	fragment Metric on Metric {
		metricName
		required
	}
`;

const MetricTierItemFragment = gql`
	fragment MetricTierItem on MetricTierItem {
		numberRequired
		missed {
			...InputValue
		}
		attained {
			...InputValue
		}
	}
`;

const MakeMetricsFragment = gql`
	fragment MakeMetrics on MakeMetrics {
		reference
		percentOfTotal
		metrics {
			...Metric
		}
		tiers {
			...MetricTierItem
		}
	}
	${MetricTierItemFragment}
`;

const CustomWeightsFragment = gql`
	fragment CustomWeights on CustomWeights {
		make
		weight
	}
`;

const WeightingFragment = gql`
	fragment Weighting on Weighting {
		weightingType
		isSurveyParticipation
		customWeights {
			...CustomWeights
		}
	}
	${CustomWeightsFragment}
`;

// Call Activity Definitions
const CallActivityTiersFragment = gql`
	fragment CallActivityTier on CallActivityTier {
		from {
			...InputValue
		}
		to {
			...InputValue
		}
		commissions {
			...CommissionTiers
		}
	}
`;

const CallActivityPerformanceFragment = gql`
	fragment CallActivityPerformance on CallActivityPerformanceForm {
		tiers {
			...CallActivityTier
		}
		individual
		hasQueues
		grossProfitSource
		evaluateBy
	}
	${CallActivityTiersFragment}
`;

// Longevity definition
const LongevityFragment = gql`
	fragment Longevity on LongevityItemTiers {
		from {
			...InputValue
		}
		to {
			...InputValue
		}
		amount {
			...InputValue
		}
		allowance {
			...InputValue
		}
	}
`;

// Top Performer bonus commission
const TopPerformerBonusFragment = gql`
	fragment TopPerformerBonus on TopPerformerBonusCommission {
		input
		value
	}
`;

// Customer satisfaction store
const StoreMakeMetricsFragment = gql`
	fragment StoreMakeMetrics on StoreMakeMetrics {
		make
		oemReference
		version
		storeMakeMetrics {
			...MakeMetrics
		}
	}
	${MakeMetricsFragment}
`;

const CustomerSatisfactionStoreFragment = gql`
	fragment CustomerSatisfactionStore on CustomerSatisfactionStore {
		makeMetrics {
			...StoreMakeMetrics
		}
		weighting {
			...Weighting
		}
	}
	${StoreMakeMetricsFragment}
	${WeightingFragment}
`;

const VolumeBonusDefinitionFragment = gql`
	fragment VolumeBonusDefinition on VolumeBonusDefinition {
		make
		tiers {
			...BonusesTiers
		}
		applyTiersRetroactively
		subjectToReputation
		forgiveness {
			...InputValue
		}
		adjustments {
			...AdjustmentsTiers
		}
	}
`;

const VolumeBonusFragment = gql`
	fragment VolumeBonus on MakesVolumeBonus {
		bonuses {
			...VolumeBonusDefinition
		}
		preOwned {
			...VolumeBonusDefinition
		}
		combined {
			...VolumeBonusDefinition
		}
		newAndPreOwnedRequired
		combineFranchises
		globalUnitCounts
	}
	${VolumeBonusDefinitionFragment}
	${AdjustmentsTiersFragment}
`;

const PaymentFragment = gql`
	fragment Payment on Payment {
		payType
		payLabel
		global
	}
`;

const CommissionPaymentFragment = gql`
	fragment CommissionPayment on CommissionPayment {
		payment {
			...Payment
		}
		commission {
			...InputValue
		}
	}
	${PaymentFragment}
`;

const TierCommissionsPaymentFragment = gql`
	fragment TierCommissionsPayment on TierCommissionsPayment {
		from {
			...InputValue
		}
		to {
			...InputValue
		}
		commissions {
			...CommissionPayment
		}
	}
	${CommissionPaymentFragment}
`;

const ELRAndHROTabContentFragment = gql`
	fragment ELRAndHROTabContent on ELRAndHROBonusContent {
		_id
		global
		relativeToStore
		option
		tiers {
			from {
				...InputValue
			}
			to {
				...InputValue
			}
			bonuses {
				bonus {
					...InputValue
				}
				from {
					...InputValue
				}
				to {
					...InputValue
				}
			}
		}
	}
`;

const ELRAndHROBonusFragment = gql`
	fragment ELRAndHROBonus on ELRAndHROBonus {
		_id
		customer {
			...ELRAndHROTabContent
		}
		internal {
			...ELRAndHROTabContent
		}
		warranty {
			...ELRAndHROTabContent
		}
	}
	${ELRAndHROTabContentFragment}
`;

const VariableOperationsGpsBonusTiersFragment = gql`
	fragment VariableOperationsGpsBonusTiers on VariableOperationsGpsBonusTiers {
		_id
		make
		tiers {
			...BonusesTiers
		}
		evaluationType
	}
	${BonusesTiersFragment}
`;

const VariableOperationsGpsBonusSetupFragment = gql`
	fragment VariableOperationsGpsBonusSetup on VariableOperationsGpsBonusSetup {
		_id
		newVehicles {
			...VariableOperationsGpsBonusTiers
		}
		preOwned {
			...VariableOperationsGpsBonusTiers
		}
		combined {
			...VariableOperationsGpsBonusTiers
		}
		combineFranchises
		globalUnitCounts
	}
	${VariableOperationsGpsBonusTiersFragment}
`;

export {
	AdjustmentsTiersFragment,
	BonusDefinitionFragment,
	BonusesTiersFragment,
	CallActivityPerformanceFragment,
	CommissionMakeTiersFragment,
	CommissionsTiersFragment,
	CommissionTiersFragment,
	CustomerSatisfactionStoreFragment,
	InputValueFragment,
	LongevityFragment,
	MakeMetricsFragment,
	MetricFragment,
	MetricTierItemFragment,
	ModelYearsFragment,
	TierCommissionsPaymentFragment,
	TopPerformerBonusFragment,
	UserFullNameFragment,
	VolumeBonusFragment,
	WeightingFragment,
	ELRAndHROBonusFragment,
	VariableOperationsGpsBonusSetupFragment,
};
